import { get, del, post, put, upload } from '@api/request';

export async function fetchPropertySales(
  { appraisalService },
  authenticationToken,
  { propertyId },
) {
  const url = `${appraisalService}/appraisal/property/${propertyId}/sales`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function deletePropertySale(
  { appraisalService },
  authenticationToken,
  { saleID },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export async function fetchSale(
  { appraisalService },
  authenticationToken,
  { saleID },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSaleCodes(
  { appraisalService, assessmentService },
  authenticationToken,
  { codeType, name, year },
) {
  if (name === 'Land Pricing Table Model') {
    let url = `${appraisalService}/appraisal/modelland?modelYr=${year}`;
    const { body } = await get(authenticationToken, url);
    return body;
  } else if (codeType === 'Assessment') {
    let url = `${assessmentService}/assessment/taxingunits/ui?type=${name}`;
    const { body } = await get(authenticationToken, url);
    return body;
  } else {
    let url = `${appraisalService}/appraisal/codes?type=${codeType}&name=${name}`;
    if (year) {
      url = `${url}&year=${year}`;
    }
    const { body } = await get(authenticationToken, url);
    return body;
  }
}

export async function createPropertySale(
  { appraisalService },
  authenticationToken,
  { payload },
) {
  const url = `${appraisalService}/appraisal/sales`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function updatePropertySale(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function createSaleProperty(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/property`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function fetchSaleProperties(
  { appraisalService },
  authenticationToken,
  { saleID },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/property`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function deleteSaleProperty(
  { appraisalService },
  authenticationToken,
  { salePropertyID },
) {
  const url = `${appraisalService}/appraisal/sale/property/${salePropertyID}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export const fetchFiles = async (
  { appraisalService },
  authenticationToken,
  { saleID },
) => {
  const url = `${appraisalService}/appraisal/sale/${saleID}/files`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const createFile = async (
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) => {
  const url = `${appraisalService}/appraisal/sale/${saleID}/files`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export async function updateFile(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/files`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export const deleteFile = async (
  { appraisalService },
  authenticationToken,
  { saleID, s3ID, payload },
) => {
  const url = `${appraisalService}/appraisal/sale/${saleID}/file/${s3ID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const getFile = async (
  { appraisalService },
  authenticationToken,
  { saleID, s3ID },
) => {
  const url = `${appraisalService}/appraisal/sale/${saleID}/file/${s3ID}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export async function setSaleDeed(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/setdeed`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function removeSaleDeed(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/removedeed`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function populateSaleFromDeed(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/populatefromdeed`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function fetchSaleRatio(
  { appraisalService },
  authenticationToken,
  { saleID, saleYear, salePrice, salePriceAdjusted },
) {
  const urlParam =
    salePrice && salePriceAdjusted
      ? `?salePrice=${salePrice}&salePriceAdjusted=${salePriceAdjusted}`
      : '';
  const url = `${appraisalService}/appraisal/sale/${saleID}/ratio/${saleYear}${urlParam}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSaleFinance(
  { appraisalService },
  authenticationToken,
  { saleID },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/finance`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateSaleFinance(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/finance`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function fetchSaleReport(
  { appraisalService },
  authenticationToken,
  { saleID },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/report`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateSaleReport(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/report`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function fetchTaxingUnitsUI(
  { assessmentService },
  authenticationToken,
  { uiType },
) {
  const url = `${assessmentService}/assessment/taxingunits/ui?type=${uiType}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSaleCharacteristics(
  { appraisalService },
  authenticationToken,
  { saleID },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/characteristics`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function updateSaleCharacteristics(
  { appraisalService },
  authenticationToken,
  { saleID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/${saleID}/characteristics`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

// sale search
export async function fetchSaleSearchConfig(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/config/salesearch`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSearchConfigResults(
  { appraisalService },
  authenticationToken,
) {
  const url = `${appraisalService}/appraisal/config/salesearchresults`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function searchSales(
  { appraisalService },
  authenticationToken,
  { payload, page = 1, pageSize = 50 },
) {
  let url = `${appraisalService}/appraisal/sales/search`;
  if (page && pageSize) {
    url += `?page=${page}&pageSize=${pageSize}`;
  }
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function fetchSaleSearchParams(
  { appraisalService },
  authenticationToken,
  { userName },
) {
  const queryParam = userName ? `?userName=${userName}` : '';
  const url = `${appraisalService}/appraisal/sales/searchparameters${queryParam}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSaleSearchParam(
  { appraisalService },
  authenticationToken,
  { searchParamID },
) {
  const url = `${appraisalService}/appraisal/sales/searchparameter/${searchParamID}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function insertSaleSearchParam(
  { appraisalService },
  authenticationToken,
  { payload },
) {
  const url = `${appraisalService}/appraisal/sales/searchparameters`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function updateSaleSearchParam(
  { appraisalService },
  authenticationToken,
  { searchParamID, payload },
) {
  const url = `${appraisalService}/appraisal/sales/searchparameter/${searchParamID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function duplicateSaleSearchParam(
  { appraisalService },
  authenticationToken,
  { searchParamID },
) {
  const url = `${appraisalService}/appraisal/sales/searchparameter/${searchParamID}/duplicate`;
  const { body } = await post(authenticationToken, url);
  return body;
}

export async function deleteSaleSearchParam(
  { appraisalService },
  authenticationToken,
  { searchParamID },
) {
  const url = `${appraisalService}/appraisal/sales/searchparameter/${searchParamID}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export async function captureTimeOfSale(
  { appraisalService },
  authenticationToken,
  { salePropertyID, payload },
) {
  const url = `${appraisalService}/appraisal/sale/property/${salePropertyID}/capturetimeofsale`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function deleteSaleVersion(
  { appraisalService },
  authenticationToken,
  { salePropertyID },
) {
  const url = `${appraisalService}/appraisal/sale/property/${salePropertyID}/removetimeofsale`;
  const { body } = await post(authenticationToken, url);
  return body;
}
